/**
 * Service worker lifecycle + the "flush pending writes" coordination seam.
 *
 * This module owns two responsibilities for the offline-reads feature:
 *
 *  1. Register the app-shell service worker (see `service-worker.ts`) so the
 *     SPA BOOTS with no signal. The shell only — survey data is not available
 *     offline since this app moved to `memoryLocalCache()` (#3775).
 *
 *  2. Provide the SEAM that WAVE 3 / S5 (the offline save/sync path) wires into
 *     so in-flight survey edits are flushed to the mutation queue before the app
 *     goes away or a new worker takes over. Audit finding D3 warns that a
 *     service-worker auto-update mid-session can silently discard unsaved edits;
 *     this is the mechanism that prevents it.
 *
 * IMPORTANT (scope boundary): this module does NOT know how to save a survey.
 * It only fires registered flush handlers at the right moments. S5 registers the
 * real flush (drain MutationQueue / persist the wizard draft) via
 * `registerFlushHandler`. Until then the handler set is empty and these hooks
 * are harmless no-ops.
 */

import * as Sentry from '@sentry/react';
import * as serviceWorkerRegistration from '../../serviceWorkerRegistration';
import { runFlushHandlers } from './flushHandlers';

// The flush seam itself now lives in flushHandlers.ts (standalone, no CRA
// registration import) so the shared appVersion update path can use it in all
// four apps. Re-exported here so existing consumers keep their import site.
export { registerFlushHandler, runFlushHandlers } from './flushHandlers';
export type { FlushHandler } from './flushHandlers';

/** Notified when a new worker version has installed and is waiting. */
type UpdateListener = (registration: ServiceWorkerRegistration) => void;

const updateListeners = new Set<UpdateListener>();

let waitingRegistration: ServiceWorkerRegistration | null = null;
let unloadFlushWired = false;

/**
 * Subscribe to "a new version is available" (worker installed and waiting).
 * If a version is already waiting, the listener is called immediately.
 * Returns an unsubscribe function.
 *
 * A future PR (or S5) can use this to surface a "reload for the latest version"
 * prompt whose confirm handler calls {@link applyUpdate}.
 */
export function onUpdateAvailable(listener: UpdateListener): () => void {
  updateListeners.add(listener);
  if (waitingRegistration) {
    listener(waitingRegistration);
  }
  return () => {
    updateListeners.delete(listener);
  };
}

/**
 * Flush pending edits, then hand control to the waiting worker and reload so
 * the freshest assets take effect. Safe to call when no update is waiting — it
 * flushes and reloads. This is the ONLY sanctioned way to activate a new
 * worker; the worker never calls `skipWaiting()` on its own (see
 * `service-worker.ts`), which is what protects in-flight edits.
 */
export async function applyUpdate(): Promise<void> {
  await runFlushHandlers();

  const waiting = waitingRegistration?.waiting;
  if (!waiting || !('serviceWorker' in navigator)) {
    window.location.reload();
    return;
  }

  // Reload once the new worker takes control so the page runs its assets.
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => {
      window.location.reload();
    },
    { once: true }
  );

  waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Fire flush handlers when the app is being hidden or torn down. `pagehide` and
 * `visibilitychange -> hidden` are the reliable signals on mobile (they fire on
 * app-switch / lock, unlike `beforeunload`). Not awaitable here — handlers do
 * best-effort synchronous work.
 */
function wireUnloadFlush(): void {
  if (unloadFlushWired || typeof window === 'undefined') return;
  unloadFlushWired = true;

  const flushOnHide = (): void => {
    void runFlushHandlers();
  };

  window.addEventListener('pagehide', flushOnHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushOnHide();
    }
  });
}

// A wrapper injected by another script names its frame differently ("ServiceWorkerContainer.register", or on Android Chrome
// "wrsParams.serviceWorkers.navigator.serviceWorker.register") but always runs from an `<anonymous>` script. The name must start
// at a word edge, so "fooserviceWorker.register" is not a match.
const INJECTED_REGISTER_FRAME = /(?:^|[\s.])(?:ServiceWorkerContainer|serviceWorker)\.register \(<anonymous>:\d+:\d+\)/m;

/**
 * True only for the one rejection the injected wrapper produces: an `Error` whose message is exactly "Rejected" AND whose stack runs
 * through an `<anonymous>` register frame. Both are required. The message test is what keeps a REAL failure that merely passes through
 * the wrapper reported: a native registration failure (SecurityError, bad script MIME, fetch failure, InvalidStateError) has a
 * descriptive name or message, and its stack also contains the wrapper's frame, so the frame alone would silence it.
 * NAMED LIMIT: a real failure that the wrapper itself replaces with a bare `Error("Rejected")` is indistinguishable and is suppressed.
 */
export function isInjectedRegisterRejection(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name !== 'Error' || error.message !== 'Rejected') return false;
  return typeof error.stack === 'string' && INJECTED_REGISTER_FRAME.test(error.stack);
}

/** Whether the app-shell service worker registered (#4029); 'failed' means offline boot is unavailable. */
export const getServiceWorkerRegistrationState = serviceWorkerRegistration.getServiceWorkerRegistrationState;

/**
 * Initialize offline support: wire the flush-on-hide seam and register the
 * app-shell service worker. Call once at app startup (replaces the old
 * `serviceWorkerRegistration.unregister()`). Registration itself only runs in
 * production builds (guarded inside `register`).
 */
export function initServiceWorker(): void {
  wireUnloadFlush();

  serviceWorkerRegistration.register({
    onUpdate: (registration) => {
      waitingRegistration = registration;
      updateListeners.forEach((listener) => listener(registration));
    },
    // #4029: a failed registration is reported with what the bare `Error: Rejected` lacked —
    // the error name and the document's lifecycle state at the moment it failed — so the
    // boot-race hypothesis can be confirmed or ruled out from Sentry alone. These fire before
    // sign-in, so this goes straight to Sentry rather than through an auth-dependent logger.
    onError: (error, { readyState }) => {
      // OFFICEHUB-CLIENT-5E: a script injected into the page can wrap register() and reject it with a bare
      // "Error: Rejected" (see isInjectedRegisterRejection). That is not our registration failing, so it is NOT sent to Sentry
      // as an issue (an info-level captureException still opened an unresolved issue the zero-Sentry measure counted). It stays
      // visible as a breadcrumb on the next event, and in the console, so a session with no later event still shows it.
      if (isInjectedRegisterRejection(error)) {
        console.info('[service-worker] register() was rejected through an injected wrapper; not reported as an app error', { readyState });
        Sentry.addBreadcrumb({
          category: 'service-worker-injected',
          level: 'info',
          message: 'register() rejected through an injected wrapper',
          data: { readyState, visibilityState: document.visibilityState },
        });
        return;
      }
      Sentry.captureException(error, {
        tags: { area: 'service-worker', swRegistration: 'failed' },
        extra: {
          readyState,
          errorName: error instanceof Error ? error.name : typeof error,
          visibilityState: document.visibilityState,
        },
      });
    },
  });
}
